// Approving devices from an active browser (docs/07 "Linked devices"): every device the API
// lists as pending, with the fingerprint to compare against the one the new device shows,
// and Approve/Reject sent as requests signed by this browser's auth key. A pending device
// cannot open a session or sign until one of these goes through.
import { Check, X } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "rizzui";

import { api, type Device } from "../api/client";
import { devicesQuery, unwrap } from "../api/queries";
import { useT } from "../i18n";
import { toast } from "../layout/toast";
import { ago } from "../pages/format";
import { base64ToBytes } from "../proto/bytes";
import type { SigAlg } from "../proto/frames";
import type { Proto } from "../proto/index";
import { signedBody } from "../secrets/requests";
import { WidgetCard } from "../ui/Card";
import { Pill } from "../ui/status";
import { DeviceIcon } from "./DeviceIcon";
import { kindLabel } from "./kinds";
import { deviceFingerprint } from "./fingerprint";
import { FingerprintChips } from "./FingerprintChips";
import type { LinkedDevice } from "./link";

type Decision = "approve" | "reject";

export interface PendingDevicesProps {
  proto: Proto;
  /** This browser, which signs the decisions. */
  device: LinkedDevice;
  className?: string;
}

export function PendingDevices({ proto, device, className }: PendingDevicesProps) {
  const t = useT().devices.pending;
  const query = useQuery(devicesQuery());
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<string>();
  const pending = (query.data ?? []).filter((d) => d.status === "pending");
  if (pending.length === 0) return null;

  async function decide(target: Device, decision: Decision) {
    setBusy(target.id);
    try {
      const path = `/api/devices/${target.id}/${decision}` as const;
      const signer = { proto, deviceId: device.deviceId, key: device.keys.auth.privateKey };
      const body = await signedBody(signer, "POST", path, {});
      const params = { path: { device_id: target.id } };
      await unwrap(
        decision === "approve"
          ? api.POST("/api/devices/{device_id}/approve", { params, body })
          : api.POST("/api/devices/{device_id}/reject", { params, body }),
      );
      toast(decision === "approve" ? t.approved(target.name) : t.rejected(target.name));
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), "warn");
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <WidgetCard
      className={className}
      label={t.label}
      title={t.title}
      action={<Pill tone="orange">{t.count(pending.length)}</Pill>}
    >
      <div className="space-y-3">
        {pending.map((d) => (
          <div key={d.id} data-testid="pending-device" className="rounded-xl border border-orange/40 bg-orange-lighter/40 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-0 text-gray-800 dark:bg-gray-100">
                <DeviceIcon kind={d.kind} />
              </span>
              <div className="min-w-[10rem] flex-1">
                <div className="font-semibold text-gray-900">{d.name}</div>
                <div className="text-xs text-gray-500">
                  {t.requested(kindLabel(d.kind), ago(d.created_at))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="gap-1" disabled={busy === d.id} onClick={() => void decide(d, "reject")}>
                  <X weight="bold" className="h-3.5 w-3.5" /> {t.reject}
                </Button>
                <Button size="sm" className="gap-1" disabled={busy === d.id} onClick={() => void decide(d, "approve")}>
                  <Check weight="bold" className="h-3.5 w-3.5" /> {t.approve}
                </Button>
              </div>
            </div>
            <div className="mt-3 text-xs text-gray-600">{t.shouldShow}</div>
            <FingerprintChips
              className="mt-1.5"
              testId="pending-fingerprint"
              value={deviceFingerprint(proto, d.auth_alg as SigAlg, base64ToBytes(d.auth_public_key))}
            />
          </div>
        ))}
      </div>
      <p className="mb-0 mt-3 text-xs text-gray-500">
        {t.note}
      </p>
    </WidgetCard>
  );
}
